import { cn } from '@my/ui';

/** Max additional days the Horizon control offers beyond today. */
export const MAX_HORIZON_EXTRA_DAYS = 5;

/**
 * Shared WorkView **Horizon** control — a fixed "Today" base chip plus an
 * additional-days dropdown ("-" / +1…+5). `value` is the number of extra days
 * beyond today (0 = today only); the range is cumulative (today..today+value).
 * The control fills blue once extra days are on, matching the active-toggle
 * look on the WorkView bars. Used by Bookings, Plan and Manifesting so the
 * three stay identical.
 */
export function HorizonField({
  value,
  onChange,
  compact = false,
}: {
  value: number;
  onChange: (extraDays: number) => void;
  /** The small size, matching the 11px dropdowns of a compact bar (Plan). */
  compact?: boolean;
}) {
  const active = value > 0;
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
        Horizon
      </span>
      <div
        className={cn(
          'inline-flex overflow-hidden rounded-md border shadow-sm',
          active ? 'border-blue-600' : 'border-slate-200',
        )}
      >
        <span
          className={cn(
            'bg-blue-600 font-medium text-white',
            compact ? 'px-2 py-1 text-[11px]' : 'px-3 py-1 text-sm',
          )}
        >
          Today
        </span>
        <select
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          aria-label="Additional days"
          className={cn(
            'cursor-pointer px-1.5 py-1 focus:outline-none',
            compact ? 'text-[11px]' : 'text-sm',
            active ? 'bg-blue-600 font-medium text-white' : 'bg-white text-slate-600 hover:bg-slate-50',
          )}
        >
          {/* Options carry explicit colours so the open list stays readable
              even when the closed control is white-on-blue. */}
          <option value={0} className="bg-white text-slate-700">
            -
          </option>
          {Array.from({ length: MAX_HORIZON_EXTRA_DAYS }, (_, i) => i + 1).map((n) => (
            <option key={n} value={n} className="bg-white text-slate-700">
              +{n}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
